import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  ErrorResponse,
  offensiveTextDetected,
  PaginatedResponse,
} from '../../api';
import { LikeType } from '../../common/model/LikeType';
import { decrementPostReplyCount } from '../posts';
import {
  createComment,
  createReplyToComment,
  deleteComment,
  fetchComment,
  fetchCommentsPaginated,
  fetchModerationReasons,
  fetchRepliesToCommentPaginated,
  fetchReply,
  fetchSelectedComment,
  moderatorDeleteComment,
  moderatorEditComment,
  updateComment,
  updateLikeComment,
} from './actions';
import { Reply } from './models';
import { ModerationReason } from './models/ModerationReason';

const likeUnlikeComment = (comment: Reply, increment: boolean) => {
  if (comment.member) {
    comment.member.memberLike = increment;
  }
  comment.memberLike = increment;
};

const incrementDecrementSentiment = (
  comment: Reply,
  sentiment: LikeType,
  increment: boolean
) => {
  const currentLikeValue =
    (comment.member?.memberLikeValue as LikeType | undefined) ??
    (comment.memberLikeValue as LikeType | undefined);
  const incrementValue = currentLikeValue === sentiment ? -1 : 1;

  if (currentLikeValue && currentLikeValue !== sentiment) {
    incrementDecrementSentiment(
      comment,
      currentLikeValue as LikeType,
      !increment
    );
  }

  switch (sentiment) {
    case LikeType.like:
      comment.likeCount =
        comment.likeCount + incrementValue >= 0
          ? comment.likeCount + incrementValue
          : comment.likeCount;
      break;
    case LikeType.care:
      comment.careCount =
        comment.careCount + incrementValue >= 0
          ? comment.careCount + incrementValue
          : comment.careCount;
      break;
    case LikeType.helpful:
      comment.helpfulCount =
        comment.helpfulCount + incrementValue >= 0
          ? comment.helpfulCount + incrementValue
          : comment.helpfulCount;
      break;

    default:
      break;
  }
};

export interface CommentState {
  comments: {
    [postId: string]: PaginatedResponse<Reply[]>;
  };
  repliesToComment: {
    [commentId: string]: PaginatedResponse<Reply[]>;
  };
  loadingComments: {
    [postId: string]: boolean;
  };
  loadingRepliesToComment: {
    [commentId: string]: boolean;
  };
  loadingCreateComment: {
    [postId: string]: boolean;
  };
  loadingUpdateComment: {
    [postId: string]: boolean;
  };
  loadingCreateReplyToComment: {
    [commentId: string]: boolean;
  };
  loadingUpdateReplyToComment: {
    [commentId: string]: boolean;
  };
  focusedComment?: Reply;
  selectedComment?: Reply;
  selectedCommentLoading?: boolean;
  selectedCommentError?: ErrorResponse;
  focusedReplyToComment?: Reply;
  isStickyKeyboardShown?: boolean;
  editedReply?: Reply;
  moderationReasons: ModerationReason[];
}

const initialState: CommentState = {
  comments: {},
  repliesToComment: {},
  loadingComments: {},
  loadingRepliesToComment: {},
  loadingCreateComment: {},
  loadingUpdateComment: {},
  loadingCreateReplyToComment: {},
  loadingUpdateReplyToComment: {},
  focusedComment: undefined,
  //TODO inspect if this is further needed across the apps
  selectedComment: undefined,
  selectedCommentLoading: undefined,
  selectedCommentError: undefined,
  focusedReplyToComment: undefined,
  isStickyKeyboardShown: undefined,
  editedReply: undefined,
  moderationReasons: [],
};

const postComments = createSlice({
  name: 'postComments',
  initialState,
  reducers: {
    clearPostComments: (state) => {
      state.comments = {};
    },
    clearCommentsByPostId: (state, action: PayloadAction<string>) => {
      delete state.comments[action.payload];
    },
    clearCommentsByReplyId: (
      state,
      action: PayloadAction<{ commentId: string; replyId: string }>
    ) => {
      const commentId = action.payload.commentId;
      const replyId = action.payload.replyId;

      const actualReplies = state.repliesToComment[commentId]?.data;

      const refreshedRepliesToComment = actualReplies.filter(
        (reply) => reply.id !== replyId
      );

      state.repliesToComment[commentId].data =
        refreshedRepliesToComment ?? actualReplies;
    },
    clearFocusedComment: (state) => {
      state.focusedComment = undefined;
    },
    clearSelectedComment: (state) => {
      state.selectedComment = undefined;
    },
    clearFocusedReplyToComment: (state) => {
      state.focusedReplyToComment = undefined;
    },
    clearRepliesToComment: (state) => {
      state.repliesToComment = {};
    },
    clearReplyFromComment: (state, action: PayloadAction<string>) => {
      delete state.repliesToComment[action.payload];
    },
    incrementCommentsCount: (
      state,
      action: PayloadAction<{
        parentId: string;
        targetId: string;
      }>
    ) => {
      const index = state.comments[action.payload.parentId].data.findIndex(
        (el) => el.id === action.payload.targetId
      );
      if (index >= 0) {
        state.comments[action.payload.parentId].data[index].replyCount += 1;
      }

      if (
        state.selectedComment &&
        state.selectedComment.id === action.payload.targetId
      ) {
        state.selectedComment.replyCount += 1;
      }
    },
    decrementCommentsCount: (
      state,
      action: PayloadAction<{
        parentId: string;
        targetId: string;
      }>
    ) => {
      const index = state.comments[action.payload.parentId].data.findIndex(
        (el) => el.id === action.payload.targetId
      );
      if (
        index >= 0 &&
        state.comments[action.payload.parentId].data[index].replyCount > 0
      ) {
        state.comments[action.payload.parentId].data[index].replyCount -= 1;
      }

      if (
        state.selectedComment &&
        state.selectedComment.id === action.payload.targetId &&
        state.selectedComment.replyCount > 0
      ) {
        state.selectedComment.replyCount -= 1;
      }
    },
    showStickyInput: (state) => {
      state.isStickyKeyboardShown = true;
    },
    hideStickyInput: (state) => {
      state.isStickyKeyboardShown = false;
    },
    setEditedReply: (state, action: PayloadAction<Reply | undefined>) => {
      state.editedReply = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommentsPaginated.pending, (state, action) => {
        state.loadingComments[action.meta.arg.postId] = true;
      })
      .addCase(fetchCommentsPaginated.fulfilled, (state, action) => {
        let postReplies: Reply[] = [];
        // if the user inserted a comment without loading anything else, then this comment is cached with no continuation token.
        // therefore, we clear the cached list before appending the new results.
        if (
          state.comments[action.meta.arg.postId]?.data.length !== 0 &&
          !state.comments[action.meta.arg.postId]?.xMsContinuationToken
        ) {
          postReplies = [];
        } else {
          postReplies = state.comments[action.meta.arg.postId]?.data || [];
        }
        postReplies?.push(
          ...action.payload.data.filter((reply) => reply.replyId === null) // TODO filtering should be done on backend. remove it when ready
        );
        state.comments[action.meta.arg.postId] = {
          data: postReplies,
          xMsContinuationToken: action.payload?.xMsContinuationToken,
        };
        state.loadingComments[action.meta.arg.postId] = false;
      })

      .addCase(fetchRepliesToCommentPaginated.pending, (state, action) => {
        state.loadingRepliesToComment[action.meta.arg.commentId] = true;
      })
      .addCase(fetchRepliesToCommentPaginated.fulfilled, (state, action) => {
        const repliesToComment =
          state.repliesToComment[action.meta.arg.commentId]?.data || [];
        repliesToComment?.push(...action.payload.data);
        state.repliesToComment[action.meta.arg.commentId] = {
          data: repliesToComment,
          xMsContinuationToken: action.payload?.xMsContinuationToken,
        };
        state.loadingRepliesToComment[action.meta.arg.commentId] = false;
      })

      .addCase(createComment.pending, (state, action) => {
        state.loadingCreateComment[action.meta.arg.postId] = true;
      })
      .addCase(createComment.rejected, (state, action) => {
        state.loadingCreateComment[action.meta.arg.postId] = false;
      })
      .addCase(createComment.fulfilled, (state, action) => {
        state.loadingCreateComment[action.meta.arg.postId] = false;
        if (!state.comments[action.meta.arg.postId])
          state.comments[action.meta.arg.postId] = {
            data: [],
          };
        state.comments[action.meta.arg.postId].data.unshift(action.payload);
      })

      .addCase(createReplyToComment.pending, (state, action) => {
        state.loadingCreateReplyToComment[action.meta.arg.commentId] = true;
      })
      .addCase(createReplyToComment.rejected, (state, action) => {
        state.loadingCreateReplyToComment[action.meta.arg.commentId] = false;
        if (offensiveTextDetected(action)) {
          const index = state.comments[action.meta.arg.postId].data.findIndex(
            (el) => el.id === action.meta.arg.commentId
          );
          if (
            index >= 0 &&
            state.comments[action.meta.arg.postId].data[index].replyCount > 0
          ) {
            state.comments[action.meta.arg.postId].data[index].replyCount -= 1;
          }

          if (
            state.selectedComment &&
            state.selectedComment.id === action.meta.arg.commentId &&
            state.selectedComment.replyCount > 0
          ) {
            state.selectedComment.replyCount -= 1;
          }
        }
      })
      .addCase(createReplyToComment.fulfilled, (state, action) => {
        state.loadingCreateReplyToComment[action.meta.arg.commentId] = false;
        if (!state.repliesToComment[action.meta.arg.commentId]) {
          state.repliesToComment[action.meta.arg.commentId] = {
            data: [],
          };
        }
        state.repliesToComment[action.meta.arg.commentId].data.unshift(
          action.payload
        );
      })

      .addCase(updateLikeComment.pending, (state, action) => {
        const sentiment = action.meta.arg.like.sentiment;
        const commentIndex = action.meta.arg.commentId
          ? state.repliesToComment[action.meta.arg.commentId].data.findIndex(
              (reply) => reply.id === action.meta.arg.replyId
            )
          : state.comments[action.meta.arg.postId].data.findIndex(
              (reply) => reply.id === action.meta.arg.replyId
            );
        const commentItem = action.meta.arg.commentId
          ? state.repliesToComment[action.meta.arg.commentId].data[commentIndex]
          : state.comments[action.meta.arg.postId].data[commentIndex];

        const currentLikeValue =
          (commentItem.member?.memberLikeValue as LikeType | undefined) ??
          (commentItem.memberLikeValue as LikeType | undefined);
        const shouldIncrement = !(currentLikeValue === sentiment);

        incrementDecrementSentiment(commentItem, sentiment, shouldIncrement);
        likeUnlikeComment(commentItem, shouldIncrement);

        const nextLikeValue = shouldIncrement ? sentiment : undefined;
        commentItem.memberLikeValue = nextLikeValue;
        if (commentItem.member) {
          commentItem.member.memberLikeValue = nextLikeValue;
        }
        if (action.meta.arg.commentId) {
          state.repliesToComment[action.meta.arg.commentId].data[commentIndex] =
            commentItem;
          if (state.focusedReplyToComment) {
            state.focusedReplyToComment = commentItem;
          }
        } else {
          state.comments[action.meta.arg.postId].data[commentIndex] =
            commentItem;
          if (state.focusedComment) {
            state.focusedComment = commentItem;
          }
          if (state.selectedComment) {
            state.selectedComment = commentItem;
          }
        }
      })
      .addCase(updateLikeComment.fulfilled, (state, action) => {
        if (action.payload.replyId === null) {
          // updating the replies to a post
          const updatedPosts = state.comments[action.meta.arg.postId]?.data.map(
            (reply) => (reply.id === action.payload.id ? action.payload : reply)
          );
          state.comments[action.meta.arg.postId].data = updatedPosts;
          if (state.focusedComment) {
            state.focusedComment = action.payload;
          }
          if (state.selectedComment) {
            state.selectedComment = action.payload;
          }
        } else {
          // updating the replies to a reply
          if (action.payload.replyId) {
            const repliesToReply =
              state.repliesToComment[action.payload.replyId]?.data;
            if (repliesToReply && repliesToReply.length > 0) {
              const updatedReplies = repliesToReply.map((reply) =>
                reply.id === action.payload.id ? action.payload : reply
              );
              state.repliesToComment[action.payload.replyId].data =
                updatedReplies;
            }
            if (state.focusedReplyToComment) {
              state.focusedReplyToComment = action.payload;
            }
          }
        }
      })
      .addCase(updateComment.pending, (state, action) => {
        state.loadingUpdateComment[action.meta.arg.postId] = true;
      })
      .addCase(fetchComment.fulfilled, (state, action) => {
        state.focusedComment = action.payload;
      })
      .addCase(fetchComment.rejected, (state) => {
        state.focusedComment = undefined;
      })
      .addCase(fetchSelectedComment.fulfilled, (state, action) => {
        state.selectedComment = action.payload;
        state.selectedCommentLoading = false;
      })
      .addCase(fetchSelectedComment.pending, (state) => {
        state.selectedCommentLoading = true;
        state.selectedComment = undefined;
        state.selectedCommentError = undefined;
      })
      .addCase(fetchSelectedComment.rejected, (state, action) => {
        state.selectedComment = undefined;
        state.selectedCommentError = action.payload;
        state.selectedCommentLoading = false;
      })
      .addCase(fetchReply.fulfilled, (state, action) => {
        state.focusedReplyToComment = action.payload;
      })
      .addCase(fetchReply.rejected, (state) => {
        state.focusedReplyToComment = undefined;
      })
      .addCase(deleteComment.pending, (state, action) => {
        if (action.meta.arg.commentId) {
          state.repliesToComment[action.meta.arg.commentId].data =
            state.repliesToComment[action.meta.arg.commentId].data.filter(
              (item) => item.id !== action.meta.arg.replyId
            );
        } else {
          state.comments[action.meta.arg.postId].data = state.comments[
            action.meta.arg.postId
          ].data.filter((item) => item.id !== action.meta.arg.replyId);
        }
      })

      .addCase(moderatorDeleteComment.fulfilled, (state, action) => {
        let removedComment: Reply | undefined;

        if (action.meta.arg.parentCommentId) {
          const repliesToComment =
            state.repliesToComment[action.meta.arg.parentCommentId].data;
          // reply to reply - find and remove from repliesToComment
          removedComment = repliesToComment.find(
            (comment) => comment.id === action.meta.arg.commentId
          );

          // Remove the comment from the list - use commentId, not replyId
          state.repliesToComment[action.meta.arg.parentCommentId].data =
            repliesToComment.filter(
              (comment) => comment.id !== action.meta.arg.commentId
            );
          decrementPostReplyCount(action.payload.postId);
        } else {
          // reply to post - find and remove from comments
          removedComment = state.comments[action.meta.arg.postId]?.data?.find(
            (comment) => comment.id === action.meta.arg.commentId
          );

          // Remove the comment from the list
          if (state.comments[action.meta.arg.postId]?.data) {
            state.comments[action.meta.arg.postId].data = state.comments[
              action.meta.arg.postId
            ].data.filter(
              (comment) => comment.id !== action.meta.arg.commentId
            );
          }

          if (
            state.repliesToComment &&
            state.repliesToComment[action.payload.id]
          ) {
            state.repliesToComment[action.payload.id].data = [];
          }
        }

        if (removedComment) {
          removedComment.content = action.payload.content;
          removedComment.lastAction = action.payload.lastAction;
        }
      })
      .addCase(moderatorEditComment.pending, (state, action) => {
        state.loadingUpdateComment[action.meta.arg.postId] = true;
      })
      .addCase(fetchModerationReasons.fulfilled, (state, action) => {
        state.moderationReasons = action.payload;
      })

      .addMatcher(
        isAnyOf(moderatorEditComment.rejected, updateComment.rejected),
        (state, action) => {
          state.loadingUpdateComment[action.meta.arg.postId] = false;
        }
      )
      .addMatcher(
        isAnyOf(moderatorEditComment.fulfilled, updateComment.fulfilled),
        (state, action) => {
          if (action.payload.replyId) {
            const updatedReplies = state.repliesToComment[
              action.payload.replyId
            ]?.data.map((reply) =>
              reply.id === action.payload.id ? action.payload : reply
            );
            state.repliesToComment[action.payload.replyId].data =
              updatedReplies;
          } else {
            const updatedReplies = state.comments[
              action.meta.arg.postId
            ]?.data.map((reply) =>
              reply.id === action.payload.id ? action.payload : reply
            );
            state.comments[action.meta.arg.postId].data = updatedReplies;
          }
          state.loadingUpdateComment[action.meta.arg.postId] = false;
        }
      );
  },
});

export const {
  clearPostComments,
  clearCommentsByReplyId,
  clearCommentsByPostId,
  clearRepliesToComment,
  clearReplyFromComment,
  incrementCommentsCount,
  decrementCommentsCount,
  clearFocusedComment,
  clearSelectedComment,
  clearFocusedReplyToComment,
  showStickyInput,
  hideStickyInput,
  setEditedReply,
} = postComments.actions;

export default postComments.reducer;
